<template>
  <SalesPlayDrawerCard>
    <div class="drawer-section">
      <div class="drawer-sectionLabel">USE A REPORT</div>
      <label class="drawer-toggleField">
        <ToggleSwitch :model-value="useReport" @update:model-value="toggleUseReport" />
        <span class="drawer-toggleLabel">{{ useReport ? 'Find contacts using the selected Salesforce Report' : 'No report, use SOQL instead' }}</span>
      </label>
      <div v-if="useReport" class="drawer-mutedText">
        The report must be based on Contact and must include an Email column.
      </div>
      <SearchSelect
        :disabled='!useReport'
        :model-value="localReportId"
        :options="initialOption"
        :options-function="searchReports"
        placeholder="Choose a Salesforce report..."
        search-placeholder="Search reports by name..."
        :allow-clear="false"
        @update:model-value="onReportSelected"
      />
      <div v-if="useReport && validating" class="drawer-mutedText">Checking report…</div>
      <div v-else-if="useReport && validationError" class="drawer-errorText">{{ validationError }}</div>
      <div v-else-if="useReport && validationSuccess" class="drawer-successText">{{ validationSuccess }}</div>
    </div>

    <div class="drawer-section">
      <div class="drawer-sectionLabel">USE SOQL QUERY</div>
      <label class="drawer-toggleField">
        <ToggleSwitch :model-value="!useReport" @update:model-value="toggleUseSOQL" />
        <span class="drawer-toggleLabel">{{ useReport ? 'No SOQL, find contacts using a Salesforce Report' : 'Find contacts using the SOQL given below' }}</span>
      </label>
      <div v-if="!useReport" class="drawer-mutedText">
        The query must select FROM Contact and include Email.
      </div>
      <textarea
        :disabled='useReport'
        class="drawer-codeInput"
        v-model="localSoql"
        :placeholder="placeholder"
        rows="5"
        @blur="applySoql"
      />
    </div>

    <div class="drawer-section">
      <button class="btn secondary" type="button" :disabled="!localSoql.trim() || previewing || useReport" @click="onPreview">
        {{ previewing ? 'Running...' : 'Preview' }}
      </button>
    </div>

    <div v-if="previewError" class="drawer-section">
      <div class="drawer-errorText">{{ previewError }}</div>
    </div>

    <div v-if="previewRecords.length > 0" class="drawer-section">
      <div class="drawer-sectionLabel">
        PREVIEW
        <span class="drawer-previewMeta">{{ previewTotalSize }} record{{ previewTotalSize !== 1 ? 's' : '' }}{{ previewTruncated ? '+' : '' }}</span>
      </div>
      <div class="drawer-previewTable">
        <table>
          <thead>
            <tr>
              <th v-for="col in previewColumns" :key="col">{{ col }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, i) in previewRecords" :key="i">
              <td v-for="col in previewColumns" :key="col">{{ formatCell(row[col]) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

  </SalesPlayDrawerCard>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import SalesPlayDrawerCard from './SalesPlayDrawerCard.vue'
import SearchSelect from '../../../components/SearchSelect.vue'
import ToggleSwitch from '../../../components/ToggleSwitch.vue'
import { apiFetch } from '../../../services/api'
import { useSalesPlayDraft } from '../../composables/useSalesPlayDraft'
import { useBlockValidation } from '../../composables/useBlockValidation'

const props = defineProps<{
  playId: string
  pipelineId: string | null
  nodeIndex: number
}>()

const { nodes, updateNodeConfig } = useSalesPlayDraft()
const { validating, validationError, validationSuccess, validate: runValidation, reset: resetValidation } = useBlockValidation()

// ── Local working copy ──────────────────────────────────────────

const currentConfig = () => nodes.value[props.nodeIndex]?.config || {}

const localReportId = ref(currentConfig().report_id || '')
const localReportName = ref(currentConfig().report_name || '')
const localSoql = ref(currentConfig().soql || '')

const placeholder = 'SELECT Id, Email FROM Contact WHERE ...'

const useReport = ref(currentConfig().mode != 'soql')

function applyConfig() {
  const currentName = currentConfig().name
  updateNodeConfig(props.nodeIndex, {
    type: 'sfdc_champions_search',
    name: currentName || localReportName.value,
    report_id: localReportId.value,
    report_name: localReportName.value,
    soql: localSoql.value.trim(),
    mode: (useReport.value ? 'report' : 'soql'),
  })
}

function toggleUseReport(next: boolean) {
  useReport.value = next
  if (next) {
    previewRecords.value = []
    previewError.value = ''
  } else {
    resetValidation()
  }
  applyConfig()
}
function toggleUseSOQL(next: boolean) {
  toggleUseReport(!next)
}


function resetLocal() {
  const cfg = currentConfig()
  localSoql.value = cfg.soql || ''
  localReportId.value = cfg.report_id || ''
  localReportName.value = cfg.report_name || ''
  useReport.value = cfg.mode != 'soql'
  previewRecords.value = []
  previewError.value = ''
  resetValidation()
}

// Re-sync when the draft node changes (e.g. after canvas-level cancel).
watch(() => nodes.value[props.nodeIndex]?.config, () => {
  resetLocal()
}, { deep: true })

// Provide the current selection so SearchSelect can show name + ID at rest.
const initialOption = computed(() => {
  if (!localReportId.value) return []
  return [{
    value: localReportId.value,
    label: localReportName.value || localReportId.value,
    secondaryLabel: localReportId.value,
  }]
})

// ── Report search via SearchSelect ──────────────────────────────

let lastFetchedOptions: { value: string; label: string }[] = []

async function searchReports(query: string) {
  const qs = query ? `?search=${encodeURIComponent(query)}` : ''
  const resp: any = await apiFetch(`/integrations/salesforce/reports${qs}`)
  const reports = resp?.reports || []
  const opts = reports.map((r: any) => ({
    value: r.id,
    label: r.name,
    secondaryLabel: r.id,
  }))
  lastFetchedOptions = opts
  return opts
}

function onReportSelected(reportId: string) {
  if (!reportId) return
  localReportId.value = reportId
  const match = lastFetchedOptions.find((o) => o.value === reportId)
  if (match) localReportName.value = match.label
  runValidation('sfdc_champions_search', {
    type: 'sfdc_champions_search',
    mode: 'report',
    report_id: reportId,
    report_name: localReportName.value,
  })
  applyConfig()
}

// ── Preview ─────────────────────────────────────────────────────

const previewing = ref(false)
const previewError = ref('')
const previewRecords = ref<any[]>([])
const previewTotalSize = ref(0)
const previewTruncated = ref(false)

const previewColumns = computed(() => {
  if (previewRecords.value.length === 0) return []
  return Object.keys(previewRecords.value[0])
})

async function onPreview() {
  const soql = localSoql.value.trim()
  if (!soql) return
  previewing.value = true
  previewError.value = ''
  previewRecords.value = []

  try {
    const resp: any = await apiFetch('/integrations/salesforce/soql-preview', {
      method: 'POST',
      body: { soql, max_records: 10 },
    })
    previewRecords.value = resp.records || []
    previewTotalSize.value = resp.total_size || 0
    previewTruncated.value = resp.truncated || false
  } catch (e: any) {
    previewError.value = e?.message || 'Preview failed'
  } finally {
    previewing.value = false
  }
}

function formatCell(value: any): string {
  if (value === null || value === undefined) return '—'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

function applySoql() {
  if (!localSoql.value.trim()) return
  applyConfig()
}
</script>

